export type SecurityHeaderStatus = "pass" | "warning" | "fail" | "info";

export type SecurityHeaderImpact = "critical" | "high" | "medium" | "low" | "info";

export type SecurityHeaderCategory =
  | "Transport"
  | "Injection"
  | "Clickjacking"
  | "Privacy"
  | "Browser features"
  | "Cross-origin isolation"
  | "Information disclosure"
  | "CORS"
  | "Cookies"
  | "Legacy";

export type SecurityHeaderEntry = {
  name: string;
  value: string;
};

export type SecurityHeaderFinding = {
  id: string;
  header: string;
  title: string;
  category: SecurityHeaderCategory;
  status: SecurityHeaderStatus;
  impact: SecurityHeaderImpact;
  score: number;
  maxScore: number;
  summary: string;
  recommendation: string;
  evidence?: string;
  details?: string[];
};

export type RecommendedSecurityHeader = {
  name: string;
  value: string;
  reason: string;
};

export type SecurityHeaderAudit = {
  score: number;
  maxScore: number;
  grade: string;
  summary: string;
  counts: Record<SecurityHeaderStatus, number>;
  findings: SecurityHeaderFinding[];
  topFixes: SecurityHeaderFinding[];
  recommendedHeaders: RecommendedSecurityHeader[];
  recommendedHeaderBlock: string;
  nginxSnippet: string;
  nextConfigSnippet: string;
};

type HeaderLookup = Map<string, SecurityHeaderEntry>;

type AuditContext = {
  finalUrl: string;
  headers: SecurityHeaderEntry[];
};

type ScoredFindingInput = Omit<SecurityHeaderFinding, "status"> & {
  status?: SecurityHeaderStatus;
};

const validReferrerPolicies = new Set([
  "no-referrer",
  "no-referrer-when-downgrade",
  "origin",
  "origin-when-cross-origin",
  "same-origin",
  "strict-origin",
  "strict-origin-when-cross-origin",
  "unsafe-url",
]);

const safeReferrerPolicies = new Set([
  "no-referrer",
  "same-origin",
  "strict-origin",
  "strict-origin-when-cross-origin",
]);

export const recommendedSecurityHeaders: RecommendedSecurityHeader[] = [
  {
    name: "Content-Security-Policy",
    value:
      "default-src 'self'; base-uri 'self'; object-src 'none'; frame-ancestors 'self'; script-src 'self'; img-src 'self' data: https:; style-src 'self' 'unsafe-inline'; connect-src 'self'; upgrade-insecure-requests",
    reason: "Limits script, plugin, framing, and mixed-content risk. Tune sources for your app.",
  },
  {
    name: "Strict-Transport-Security",
    value: "max-age=31536000; includeSubDomains",
    reason: "Keeps browsers on HTTPS for one year, including subdomains.",
  },
  {
    name: "X-Content-Type-Options",
    value: "nosniff",
    reason: "Prevents browsers from guessing executable MIME types.",
  },
  {
    name: "Referrer-Policy",
    value: "strict-origin-when-cross-origin",
    reason: "Preserves useful analytics while avoiding path/query leakage to other origins.",
  },
  {
    name: "Permissions-Policy",
    value: "camera=(), microphone=(), geolocation=(), payment=(), usb=()",
    reason: "Disables sensitive browser features unless the site explicitly needs them.",
  },
  {
    name: "Cross-Origin-Opener-Policy",
    value: "same-origin",
    reason: "Isolates top-level documents from cross-origin opener access.",
  },
  {
    name: "Cross-Origin-Resource-Policy",
    value: "same-origin",
    reason: "Restricts no-cors embedding of resources by other origins.",
  },
];

export function recommendedSecurityHeaderBlock() {
  return recommendedSecurityHeaders
    .map((header) => `${header.name}: ${header.value}`)
    .join("\n");
}

export function recommendedNginxSnippet() {
  return recommendedSecurityHeaders
    .map((header) => `add_header ${header.name} "${header.value.replace(/"/g, '\\"')}" always;`)
    .join("\n");
}

export function recommendedNextConfigSnippet() {
  const headers = recommendedSecurityHeaders
    .map(
      (header) =>
        `        {\n          key: "${header.name}",\n          value: "${header.value.replace(/\\/g, "\\\\").replace(/"/g, '\\"')}",\n        }`,
    )
    .join(",\n");

  return `import type { NextConfig } from "next";

const nextConfig: NextConfig = {
  async headers() {
    return [
      {
        source: "/(.*)",
        headers: [
${headers},
        ],
      },
    ];
  },
};

export default nextConfig;`;
}

function normalizeHeaderName(name: string) {
  return name.trim().toLowerCase();
}

function createHeaderLookup(headers: SecurityHeaderEntry[]): HeaderLookup {
  const lookup = new Map<string, SecurityHeaderEntry>();

  for (const header of headers) {
    const key = normalizeHeaderName(header.name);
    const existing = lookup.get(key);

    lookup.set(key, {
      name: existing?.name ?? header.name,
      value: existing ? `${existing.value}, ${header.value}` : header.value,
    });
  }

  return lookup;
}

function getHeader(lookup: HeaderLookup, name: string) {
  return lookup.get(normalizeHeaderName(name))?.value.trim() ?? "";
}

function statusFromScore(score: number, maxScore: number): SecurityHeaderStatus {
  if (maxScore === 0) {
    return "info";
  }

  if (score >= maxScore) {
    return "pass";
  }

  if (score <= 0) {
    return "fail";
  }

  return "warning";
}

function finding(input: ScoredFindingInput): SecurityHeaderFinding {
  return {
    ...input,
    status: input.status ?? statusFromScore(input.score, input.maxScore),
  };
}

function parseDirectiveList(value: string) {
  const directives = new Map<string, string[]>();

  for (const part of value.split(";")) {
    const trimmed = part.trim();

    if (!trimmed) {
      continue;
    }

    const [rawName, ...rawValues] = trimmed.split(/\s+/);
    directives.set(rawName.toLowerCase(), rawValues.map((item) => item.toLowerCase()));
  }

  return directives;
}

function directiveHasSource(
  directives: Map<string, string[]>,
  directive: string,
  matcher: string | RegExp,
) {
  const values = directives.get(directive.toLowerCase()) ?? [];

  return values.some((value) =>
    typeof matcher === "string" ? value === matcher.toLowerCase() : matcher.test(value),
  );
}

function hasNonceHashOrStrictDynamic(values: string[]) {
  return values.some(
    (value) =>
      value === "'strict-dynamic'" ||
      value.startsWith("'nonce-") ||
      value.startsWith("'sha256-") ||
      value.startsWith("'sha384-") ||
      value.startsWith("'sha512-"),
  );
}

function auditCsp(lookup: HeaderLookup): SecurityHeaderFinding {
  const csp = getHeader(lookup, "Content-Security-Policy");
  const reportOnly = getHeader(lookup, "Content-Security-Policy-Report-Only");

  if (!csp) {
    return finding({
      id: "content-security-policy",
      header: "Content-Security-Policy",
      title: reportOnly ? "CSP is report-only, not enforced" : "Missing enforced Content Security Policy",
      category: "Injection",
      impact: "critical",
      score: reportOnly ? 8 : 0,
      maxScore: 22,
      summary: reportOnly
        ? "A report-only policy can collect violations, but it does not block risky resources."
        : "No enforced CSP was found, leaving the page without a central browser policy for scripts, frames, plugins, and mixed content.",
      recommendation:
        "Add an enforced Content-Security-Policy with default-src or script-src, object-src 'none', base-uri, and frame-ancestors.",
      evidence: reportOnly ? reportOnly : undefined,
    });
  }

  const directives = parseDirectiveList(csp);
  const details: string[] = [];
  let score = 22;
  const scriptValues = directives.get("script-src") ?? directives.get("default-src") ?? [];
  const styleValues = directives.get("style-src") ?? directives.get("default-src") ?? [];

  if (!directives.has("default-src") && !directives.has("script-src")) {
    score -= 7;
    details.push("Add default-src or script-src so JavaScript has a clear allowlist.");
  }

  if (
    scriptValues.includes("'unsafe-inline'") &&
    !hasNonceHashOrStrictDynamic(scriptValues)
  ) {
    score -= 5;
    details.push("Avoid unsafe-inline in script-src unless it is neutralized by nonces, hashes, or strict-dynamic.");
  }

  if (scriptValues.includes("'unsafe-eval'")) {
    score -= 4;
    details.push("Remove unsafe-eval from script-src where possible.");
  }

  if (directiveHasSource(directives, "script-src", "*") || directiveHasSource(directives, "default-src", "*")) {
    score -= 4;
    details.push("Avoid wildcard sources for script or default resources.");
  }

  if (directiveHasSource(directives, "script-src", "http:") || directiveHasSource(directives, "default-src", "http:")) {
    score -= 3;
    details.push("Avoid loading scripts or default resources over plain HTTP.");
  }

  if (!directives.has("object-src") || !directiveHasSource(directives, "object-src", "'none'")) {
    score -= 3;
    details.push("Set object-src 'none' to disable legacy plugin embeds.");
  }

  if (
    !directives.has("base-uri") ||
    (!directiveHasSource(directives, "base-uri", "'self'") &&
      !directiveHasSource(directives, "base-uri", "'none'"))
  ) {
    score -= 2;
    details.push("Set base-uri to 'self' or 'none' to prevent injected base URL rewrites.");
  }

  if (!directives.has("frame-ancestors")) {
    score -= 2;
    details.push("Add frame-ancestors to control who can embed this page.");
  }

  if (styleValues.includes("'unsafe-inline'")) {
    details.push("Inline styles are allowed. That is common, but stricter CSPs use nonces or hashes.");
  }

  const boundedScore = Math.max(6, score);

  return finding({
    id: "content-security-policy",
    header: "Content-Security-Policy",
    title: details.length ? "Content Security Policy needs tightening" : "Content Security Policy is enforced",
    category: "Injection",
    impact: details.length ? "high" : "critical",
    score: boundedScore,
    maxScore: 22,
    summary: details.length
      ? "An enforced CSP exists, but important directives or source restrictions can be improved."
      : "An enforced CSP is present with the core protections this audit checks.",
    recommendation:
      "Use a specific CSP allowlist and include default-src, object-src 'none', base-uri, and frame-ancestors.",
    evidence: csp,
    details,
  });
}

function parseHsts(value: string) {
  const directives = new Map<string, string | true>();

  for (const part of value.split(";")) {
    const trimmed = part.trim();

    if (!trimmed) {
      continue;
    }

    const [rawKey, rawValue] = trimmed.split("=");
    directives.set(rawKey.toLowerCase(), rawValue?.trim() ?? true);
  }

  return directives;
}

function auditHsts(lookup: HeaderLookup, finalUrl: string): SecurityHeaderFinding {
  const hsts = getHeader(lookup, "Strict-Transport-Security");
  const isHttps = finalUrl.toLowerCase().startsWith("https://");

  if (!isHttps) {
    return finding({
      id: "strict-transport-security",
      header: "Strict-Transport-Security",
      title: "Final URL is not HTTPS",
      category: "Transport",
      impact: "critical",
      score: 0,
      maxScore: 18,
      summary: "HSTS only protects secure HTTPS responses. This audit reached a non-HTTPS final URL.",
      recommendation: "Redirect HTTP to HTTPS, then send Strict-Transport-Security on HTTPS responses.",
      evidence: finalUrl,
    });
  }

  if (!hsts) {
    return finding({
      id: "strict-transport-security",
      header: "Strict-Transport-Security",
      title: "Missing HSTS",
      category: "Transport",
      impact: "critical",
      score: 0,
      maxScore: 18,
      summary: "No HSTS header was found on the HTTPS response.",
      recommendation: "Send Strict-Transport-Security with at least max-age=31536000 after HTTPS is reliable.",
    });
  }

  const directives = parseHsts(hsts);
  const rawMaxAge = directives.get("max-age");
  const maxAge =
    typeof rawMaxAge === "string" && /^\d+$/.test(rawMaxAge) ? Number(rawMaxAge) : Number.NaN;
  const hasIncludeSubDomains = directives.has("includesubdomains");
  const hasPreload = directives.has("preload");
  const details: string[] = [];
  let score = 18;

  if (!Number.isFinite(maxAge)) {
    score = 0;
    details.push("The max-age directive is missing or invalid.");
  } else if (maxAge === 0) {
    score = 0;
    details.push("max-age=0 disables HSTS.");
  } else if (maxAge < 31536000) {
    score = maxAge < 86400 ? 5 : 12;
    details.push("Use at least 31536000 seconds once HTTPS is stable.");
  }

  if (score > 0 && !hasIncludeSubDomains) {
    score = Math.min(score, 15);
    details.push("Add includeSubDomains only when every subdomain is HTTPS-ready.");
  }

  if (hasPreload && (!hasIncludeSubDomains || maxAge < 31536000)) {
    score = Math.min(score, 12);
    details.push("The preload directive requires max-age of at least one year and includeSubDomains.");
  }

  return finding({
    id: "strict-transport-security",
    header: "Strict-Transport-Security",
    title: details.length ? "HSTS is present but can be stronger" : "HSTS is strong",
    category: "Transport",
    impact: details.length ? "high" : "critical",
    score,
    maxScore: 18,
    summary: details.length
      ? "The response sends HSTS, but its lifetime or subdomain coverage should be reviewed."
      : "The HTTPS response sends an HSTS policy with a one-year or longer lifetime.",
    recommendation:
      "Use Strict-Transport-Security: max-age=31536000; includeSubDomains after validating all subdomains.",
    evidence: hsts,
    details,
  });
}

function auditContentTypeOptions(lookup: HeaderLookup): SecurityHeaderFinding {
  const value = getHeader(lookup, "X-Content-Type-Options");
  const isNosniff = value.toLowerCase() === "nosniff";

  return finding({
    id: "x-content-type-options",
    header: "X-Content-Type-Options",
    title: isNosniff ? "MIME sniffing protection is enabled" : "Missing nosniff protection",
    category: "Injection",
    impact: "medium",
    score: isNosniff ? 10 : 0,
    maxScore: 10,
    summary: isNosniff
      ? "Browsers are instructed to respect declared MIME types."
      : "Browsers may try to infer content types when this header is absent or invalid.",
    recommendation: "Send X-Content-Type-Options: nosniff on all responses.",
    evidence: value || undefined,
  });
}

function auditClickjacking(lookup: HeaderLookup): SecurityHeaderFinding {
  const csp = getHeader(lookup, "Content-Security-Policy");
  const xfo = getHeader(lookup, "X-Frame-Options");
  const directives = csp ? parseDirectiveList(csp) : new Map<string, string[]>();
  const frameAncestors = directives.get("frame-ancestors");
  const normalizedXfo = xfo.toLowerCase();

  if (frameAncestors?.length) {
    const tooOpen = frameAncestors.includes("*") || frameAncestors.includes("http:");

    return finding({
      id: "clickjacking-protection",
      header: "Content-Security-Policy frame-ancestors",
      title: tooOpen ? "frame-ancestors is too broad" : "Frame embedding is controlled by CSP",
      category: "Clickjacking",
      impact: tooOpen ? "high" : "medium",
      score: tooOpen ? 3 : 10,
      maxScore: 10,
      summary: tooOpen
        ? "The CSP frame-ancestors directive exists, but broad sources can still allow unsafe embedding."
        : "CSP controls which parents may embed this page.",
      recommendation: "Use frame-ancestors 'none', 'self', or a small list of trusted origins.",
      evidence: `frame-ancestors ${frameAncestors.join(" ")}`,
    });
  }

  if (normalizedXfo === "deny" || normalizedXfo === "sameorigin") {
    return finding({
      id: "clickjacking-protection",
      header: "X-Frame-Options",
      title: "Frame embedding is controlled by X-Frame-Options",
      category: "Clickjacking",
      impact: "medium",
      score: 8,
      maxScore: 10,
      summary: "X-Frame-Options is present, but CSP frame-ancestors is the modern control.",
      recommendation: "Prefer Content-Security-Policy frame-ancestors, and keep X-Frame-Options for older clients if useful.",
      evidence: xfo,
    });
  }

  return finding({
    id: "clickjacking-protection",
    header: "Content-Security-Policy frame-ancestors",
    title: xfo ? "Clickjacking header is invalid or obsolete" : "Missing clickjacking protection",
    category: "Clickjacking",
    impact: "high",
    score: 0,
    maxScore: 10,
    summary: xfo
      ? "The response includes X-Frame-Options, but the value is not DENY or SAMEORIGIN."
      : "No CSP frame-ancestors directive or valid X-Frame-Options header was found.",
    recommendation: "Add Content-Security-Policy: frame-ancestors 'self' or frame-ancestors 'none'.",
    evidence: xfo || undefined,
  });
}

function auditReferrerPolicy(lookup: HeaderLookup): SecurityHeaderFinding {
  const value = getHeader(lookup, "Referrer-Policy");

  if (!value) {
    return finding({
      id: "referrer-policy",
      header: "Referrer-Policy",
      title: "Missing explicit referrer policy",
      category: "Privacy",
      impact: "medium",
      score: 3,
      maxScore: 8,
      summary: "Modern browsers default to strict-origin-when-cross-origin, but an explicit header is clearer and more consistent.",
      recommendation: "Send Referrer-Policy: strict-origin-when-cross-origin, strict-origin, same-origin, or no-referrer.",
    });
  }

  const tokens = value
    .split(",")
    .map((item) => item.trim().toLowerCase())
    .filter(Boolean);
  const chosen = [...tokens].reverse().find((item) => validReferrerPolicies.has(item));

  if (!chosen) {
    return finding({
      id: "referrer-policy",
      header: "Referrer-Policy",
      title: "Invalid referrer policy",
      category: "Privacy",
      impact: "medium",
      score: 0,
      maxScore: 8,
      summary: "The header value does not match a recognized referrer policy token.",
      recommendation: "Use strict-origin-when-cross-origin, strict-origin, same-origin, or no-referrer.",
      evidence: value,
    });
  }

  if (safeReferrerPolicies.has(chosen)) {
    return finding({
      id: "referrer-policy",
      header: "Referrer-Policy",
      title: "Referrer policy is privacy-aware",
      category: "Privacy",
      impact: "medium",
      score: 8,
      maxScore: 8,
      summary: "The selected referrer policy limits cross-origin or downgrade leakage.",
      recommendation: "Keep the policy aligned with analytics and privacy needs.",
      evidence: value,
    });
  }

  return finding({
    id: "referrer-policy",
    header: "Referrer-Policy",
    title: chosen === "unsafe-url" ? "Unsafe referrer policy" : "Referrer policy could be stricter",
    category: "Privacy",
    impact: chosen === "unsafe-url" ? "high" : "medium",
    score: chosen === "unsafe-url" ? 0 : 5,
    maxScore: 8,
    summary:
      chosen === "unsafe-url"
        ? "unsafe-url can send full paths and query strings to other origins."
        : "The policy is valid, but stricter options reduce cross-origin information leakage.",
    recommendation: "Prefer strict-origin-when-cross-origin, strict-origin, same-origin, or no-referrer.",
    evidence: value,
  });
}

function featureDisabled(policy: string, feature: string) {
  return new RegExp(`(?:^|[,;]\\s*)${feature}\\s*=\\s*\\(\\s*\\)`, "i").test(policy);
}

function featureWildcard(policy: string, feature: string) {
  return new RegExp(`(?:^|[,;]\\s*)${feature}\\s*=\\s*\\([^)]*\\*`, "i").test(policy);
}

function auditPermissionsPolicy(lookup: HeaderLookup): SecurityHeaderFinding {
  const value = getHeader(lookup, "Permissions-Policy");
  const sensitiveFeatures = ["camera", "microphone", "geolocation"];

  if (!value) {
    return finding({
      id: "permissions-policy",
      header: "Permissions-Policy",
      title: "Missing Permissions-Policy",
      category: "Browser features",
      impact: "medium",
      score: 2,
      maxScore: 8,
      summary: "No policy was found for restricting powerful browser APIs.",
      recommendation: "Disable features the site does not need, such as camera=(), microphone=(), and geolocation=().",
    });
  }

  const disabled = sensitiveFeatures.filter((feature) => featureDisabled(value, feature));
  const wildcard = sensitiveFeatures.filter((feature) => featureWildcard(value, feature));
  const details: string[] = [];

  if (wildcard.length) {
    details.push(`Broad allowlists found for: ${wildcard.join(", ")}.`);
  }

  if (disabled.length < sensitiveFeatures.length) {
    const missing = sensitiveFeatures.filter((feature) => !disabled.includes(feature));
    details.push(`Consider disabling unused sensitive features: ${missing.join(", ")}.`);
  }

  return finding({
    id: "permissions-policy",
    header: "Permissions-Policy",
    title:
      disabled.length === sensitiveFeatures.length && !wildcard.length
        ? "Sensitive browser features are restricted"
        : "Permissions-Policy can be tightened",
    category: "Browser features",
    impact: wildcard.length ? "high" : "medium",
    score: wildcard.length ? 2 : disabled.length === sensitiveFeatures.length ? 8 : 5,
    maxScore: 8,
    summary:
      disabled.length === sensitiveFeatures.length && !wildcard.length
        ? "Camera, microphone, and geolocation are disabled by policy."
        : "A Permissions-Policy header exists, but sensitive features are not fully locked down.",
    recommendation: "Disable unused sensitive features with camera=(), microphone=(), geolocation=(), and payment=().",
    evidence: value,
    details,
  });
}

function auditCoop(lookup: HeaderLookup): SecurityHeaderFinding {
  const value = getHeader(lookup, "Cross-Origin-Opener-Policy");
  const normalized = value.toLowerCase();

  if (!value) {
    return finding({
      id: "cross-origin-opener-policy",
      header: "Cross-Origin-Opener-Policy",
      title: "Missing COOP",
      category: "Cross-origin isolation",
      impact: "low",
      score: 2,
      maxScore: 6,
      summary: "No opener policy was found for isolating top-level documents from cross-origin windows.",
      recommendation: "Use Cross-Origin-Opener-Policy: same-origin unless pop-up integrations require a looser policy.",
    });
  }

  const score = normalized === "same-origin" ? 6 : normalized.includes("allow-popups") ? 5 : 0;

  return finding({
    id: "cross-origin-opener-policy",
    header: "Cross-Origin-Opener-Policy",
    title: score ? "COOP is configured" : "COOP is ineffective",
    category: "Cross-origin isolation",
    impact: score ? "low" : "medium",
    score,
    maxScore: 6,
    summary: score
      ? "The response controls whether cross-origin documents share a browsing context group."
      : "The COOP value does not isolate this document.",
    recommendation: "Use same-origin for strongest isolation, or same-origin-allow-popups when trusted pop-ups are required.",
    evidence: value,
  });
}

function auditCorp(lookup: HeaderLookup): SecurityHeaderFinding {
  const value = getHeader(lookup, "Cross-Origin-Resource-Policy");
  const normalized = value.toLowerCase();

  if (!value) {
    return finding({
      id: "cross-origin-resource-policy",
      header: "Cross-Origin-Resource-Policy",
      title: "Missing CORP",
      category: "Cross-origin isolation",
      impact: "low",
      score: 2,
      maxScore: 5,
      summary: "No resource policy was found for controlling no-cors embedding by other origins.",
      recommendation: "Use same-origin or same-site for pages and private resources. Use cross-origin only for public assets that require it.",
    });
  }

  const score = normalized === "same-origin" || normalized === "same-site" ? 5 : normalized === "cross-origin" ? 2 : 0;

  return finding({
    id: "cross-origin-resource-policy",
    header: "Cross-Origin-Resource-Policy",
    title: score === 5 ? "CORP limits cross-origin resource use" : "CORP allows broad resource use",
    category: "Cross-origin isolation",
    impact: score === 5 ? "low" : "medium",
    score,
    maxScore: 5,
    summary:
      score === 5
        ? "The resource policy restricts which origins can embed the response."
        : "The policy is missing, invalid, or explicitly allows cross-origin loading.",
    recommendation: "Prefer Cross-Origin-Resource-Policy: same-origin or same-site unless public embedding is intended.",
    evidence: value,
  });
}

function auditCoep(lookup: HeaderLookup): SecurityHeaderFinding {
  const value = getHeader(lookup, "Cross-Origin-Embedder-Policy");
  const normalized = value.toLowerCase();

  if (!value) {
    return finding({
      id: "cross-origin-embedder-policy",
      header: "Cross-Origin-Embedder-Policy",
      title: "COEP is not enabled",
      category: "Cross-origin isolation",
      impact: "low",
      score: 2,
      maxScore: 4,
      summary: "COEP is optional for many sites, but it is required with COOP for cross-origin isolated features.",
      recommendation: "Use require-corp or credentialless only after validating third-party resources.",
    });
  }

  const score = normalized === "require-corp" || normalized === "credentialless" ? 4 : 0;

  return finding({
    id: "cross-origin-embedder-policy",
    header: "Cross-Origin-Embedder-Policy",
    title: score ? "COEP is configured" : "COEP value is not protective",
    category: "Cross-origin isolation",
    impact: score ? "low" : "medium",
    score,
    maxScore: 4,
    summary: score
      ? "The response opts into embedder policy required for cross-origin isolation."
      : "The COEP value does not enable a recognized isolation policy.",
    recommendation: "Use Cross-Origin-Embedder-Policy: require-corp or credentialless if the app needs cross-origin isolation.",
    evidence: value,
  });
}

function auditInformationDisclosure(lookup: HeaderLookup): SecurityHeaderFinding {
  const exposed = [
    "Server",
    "X-Powered-By",
    "X-AspNet-Version",
    "X-AspNetMvc-Version",
  ]
    .map((header) => [header, getHeader(lookup, header)] as const)
    .filter(([, value]) => Boolean(value));

  return finding({
    id: "information-disclosure",
    header: "Server / X-Powered-By",
    title: exposed.length ? "Technology headers are exposed" : "Technology disclosure headers are absent",
    category: "Information disclosure",
    impact: exposed.length ? "low" : "info",
    score: exposed.length ? 2 : 5,
    maxScore: 5,
    summary: exposed.length
      ? "The response exposes server or framework information that can help fingerprint the stack."
      : "No common server or framework disclosure headers were found.",
    recommendation: "Remove X-Powered-By and minimize Server/version headers at the edge or origin.",
    evidence: exposed.map(([name, value]) => `${name}: ${value}`).join("\n") || undefined,
  });
}

function auditCors(lookup: HeaderLookup): SecurityHeaderFinding {
  const allowOrigin = getHeader(lookup, "Access-Control-Allow-Origin");
  const allowCredentials = getHeader(lookup, "Access-Control-Allow-Credentials").toLowerCase();

  if (!allowOrigin) {
    return finding({
      id: "cors-policy",
      header: "Access-Control-Allow-Origin",
      title: "No broad CORS policy detected",
      category: "CORS",
      impact: "info",
      score: 4,
      maxScore: 4,
      summary: "The response does not expose a cross-origin allowlist through Access-Control-Allow-Origin.",
      recommendation: "Keep CORS scoped to the origins and methods that actually need browser access.",
    });
  }

  const wildcard = allowOrigin.trim() === "*";
  const credentialed = allowCredentials === "true";

  return finding({
    id: "cors-policy",
    header: "Access-Control-Allow-Origin",
    title:
      wildcard && credentialed
        ? "Wildcard CORS with credentials"
        : wildcard
          ? "Wildcard CORS is enabled"
          : "CORS allowlist is explicit",
    category: "CORS",
    impact: wildcard && credentialed ? "high" : wildcard ? "medium" : "info",
    score: wildcard && credentialed ? 0 : wildcard ? 2 : 4,
    maxScore: 4,
    summary:
      wildcard && credentialed
        ? "The response combines broad cross-origin access with credentialed requests."
        : wildcard
          ? "The response can be read by scripts from any origin."
          : "The response uses a specific Access-Control-Allow-Origin value.",
    recommendation: "Use a specific origin allowlist, especially for credentialed or non-public APIs.",
    evidence: [
      `Access-Control-Allow-Origin: ${allowOrigin}`,
      allowCredentials ? `Access-Control-Allow-Credentials: ${allowCredentials}` : "",
    ]
      .filter(Boolean)
      .join("\n"),
  });
}

function auditCookies(lookup: HeaderLookup): SecurityHeaderFinding[] {
  const setCookie = getHeader(lookup, "Set-Cookie");

  if (!setCookie) {
    return [];
  }

  const lower = setCookie.toLowerCase();
  const details = [
    lower.includes("secure") ? "" : "Add Secure to cookies sent over HTTPS.",
    lower.includes("httponly") ? "" : "Add HttpOnly to cookies that JavaScript does not need.",
    lower.includes("samesite") ? "" : "Add SameSite=Lax or SameSite=Strict where compatible.",
  ].filter(Boolean);

  return [
    finding({
      id: "cookie-attributes",
      header: "Set-Cookie",
      title: details.length ? "Cookie attributes should be reviewed" : "Cookie security attributes are present",
      category: "Cookies",
      impact: details.length ? "medium" : "info",
      score: 0,
      maxScore: 0,
      status: details.length ? "warning" : "info",
      summary: details.length
        ? "At least one cookie appears to be missing a common security attribute."
        : "The visible Set-Cookie header includes Secure, HttpOnly, and SameSite attributes.",
      recommendation: "Use Secure, HttpOnly, and SameSite where appropriate for session cookies.",
      evidence: setCookie,
      details,
    }),
  ];
}

function auditLegacyHeaders(lookup: HeaderLookup): SecurityHeaderFinding[] {
  const findings: SecurityHeaderFinding[] = [];
  const xssProtection = getHeader(lookup, "X-XSS-Protection");
  const expectCt = getHeader(lookup, "Expect-CT");
  const hpkp = getHeader(lookup, "Public-Key-Pins");

  if (xssProtection && xssProtection.trim() !== "0") {
    findings.push(
      finding({
        id: "x-xss-protection",
        header: "X-XSS-Protection",
        title: "Legacy XSS filter is enabled",
        category: "Legacy",
        impact: "low",
        score: 0,
        maxScore: 0,
        status: "warning",
        summary: "Modern guidance favors CSP and disabling legacy XSS auditors.",
        recommendation: "Remove X-XSS-Protection or set X-XSS-Protection: 0.",
        evidence: xssProtection,
      }),
    );
  }

  if (expectCt) {
    findings.push(
      finding({
        id: "expect-ct",
        header: "Expect-CT",
        title: "Deprecated Expect-CT header is present",
        category: "Legacy",
        impact: "low",
        score: 0,
        maxScore: 0,
        status: "info",
        summary: "Expect-CT is no longer a primary modern security control.",
        recommendation: "Remove deprecated headers unless you have a compatibility reason to keep them.",
        evidence: expectCt,
      }),
    );
  }

  if (hpkp) {
    findings.push(
      finding({
        id: "public-key-pins",
        header: "Public-Key-Pins",
        title: "Deprecated HPKP header is present",
        category: "Legacy",
        impact: "high",
        score: 0,
        maxScore: 0,
        status: "warning",
        summary: "HPKP can lock users out of a site when misconfigured and is deprecated in modern guidance.",
        recommendation: "Remove Public-Key-Pins and rely on Certificate Transparency plus modern TLS operations.",
        evidence: hpkp,
      }),
    );
  }

  return findings;
}

function gradeFromScore(score: number) {
  if (score >= 97) {
    return "A+";
  }

  if (score >= 90) {
    return "A";
  }

  if (score >= 80) {
    return "B";
  }

  if (score >= 70) {
    return "C";
  }

  if (score >= 60) {
    return "D";
  }

  return "F";
}

function summaryForGrade(grade: string, failCount: number, warningCount: number) {
  if (grade === "A+" || grade === "A") {
    return warningCount
      ? "Strong security header posture with a few refinements available."
      : "Excellent security header posture across the audited controls.";
  }

  if (grade === "B") {
    return "Good baseline security headers, but several meaningful hardening opportunities remain.";
  }

  if (grade === "C" || grade === "D") {
    return "Partial security header coverage. Prioritize the failed high-impact controls first.";
  }

  return failCount
    ? "Weak security header posture. Add the missing core headers before tuning optional policies."
    : "Security header posture needs improvement.";
}

export function auditSecurityHeaders(context: AuditContext): SecurityHeaderAudit {
  const lookup = createHeaderLookup(context.headers);
  const findings = [
    auditCsp(lookup),
    auditHsts(lookup, context.finalUrl),
    auditContentTypeOptions(lookup),
    auditClickjacking(lookup),
    auditReferrerPolicy(lookup),
    auditPermissionsPolicy(lookup),
    auditCoop(lookup),
    auditCorp(lookup),
    auditCoep(lookup),
    auditInformationDisclosure(lookup),
    auditCors(lookup),
    ...auditCookies(lookup),
    ...auditLegacyHeaders(lookup),
  ];

  const maxScore = findings.reduce((sum, item) => sum + item.maxScore, 0);
  const rawScore = findings.reduce((sum, item) => sum + item.score, 0);
  const score = maxScore ? Math.round((rawScore / maxScore) * 100) : 0;
  const grade = gradeFromScore(score);
  const counts = findings.reduce<Record<SecurityHeaderStatus, number>>(
    (result, item) => {
      result[item.status] += 1;
      return result;
    },
    { pass: 0, warning: 0, fail: 0, info: 0 },
  );
  const topFixes = findings
    .filter((item) => item.maxScore > 0 && item.status !== "pass")
    .sort((left, right) => {
      const leftGap = left.maxScore - left.score;
      const rightGap = right.maxScore - right.score;

      return rightGap - leftGap;
    })
    .slice(0, 5);

  return {
    score,
    maxScore: 100,
    grade,
    summary: summaryForGrade(grade, counts.fail, counts.warning),
    counts,
    findings,
    topFixes,
    recommendedHeaders: recommendedSecurityHeaders,
    recommendedHeaderBlock: recommendedSecurityHeaderBlock(),
    nginxSnippet: recommendedNginxSnippet(),
    nextConfigSnippet: recommendedNextConfigSnippet(),
  };
}

export function formatSecurityHeaderAuditReport(input: {
  requestedUrl: string;
  finalUrl: string;
  status: number;
  score: number;
  grade: string;
  findings: SecurityHeaderFinding[];
  topFixes: SecurityHeaderFinding[];
}) {
  const lines = [
    `Security Headers Audit`,
    `Requested URL: ${input.requestedUrl}`,
    `Final URL: ${input.finalUrl}`,
    `HTTP status: ${input.status}`,
    `Grade: ${input.grade}`,
    `Score: ${input.score}/100`,
    "",
    "Top fixes:",
    ...(input.topFixes.length
      ? input.topFixes.map((item) => `- ${item.header}: ${item.title} (${item.recommendation})`)
      : ["- No high-priority fixes found."]),
    "",
    "Findings:",
    ...input.findings.map(
      (item) =>
        `- [${item.status.toUpperCase()}] ${item.header}: ${item.summary}${
          item.evidence ? ` Evidence: ${item.evidence.replace(/\s+/g, " ")}` : ""
        }`,
    ),
  ];

  return lines.join("\n");
}
